import React, { useMemo, useState } from 'react';
import axios from 'axios';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
  closestCenter,
} from '@dnd-kit/core';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { User as UserIcon, Clock, MessageSquare } from 'lucide-react';

const PRIORITY_DOT = {
  low: '#6b7280',
  normal: '#3b82f6',
  high: '#ef4444',
};

const DraggableCard = ({ request, onOpen }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: request.id,
    data: { request },
  });
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, opacity: isDragging ? 0.4 : 1 }
    : {};
  const title = Object.values(request.form_data || {})[0] || 'Ohne Titel';
  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes} className="cursor-grab active:cursor-grabbing">
      <Card
        className="mb-2 hover:shadow-md transition-shadow border-l-4"
        style={{ borderLeftColor: request.type_color || '#6b7280' }}
        data-testid={`kanban-card-${request.id}`}
        onClick={(e) => {
          // Prevent drag-trigger from blocking the click on small movements
          if (!isDragging) onOpen?.(request);
        }}
      >
        <CardContent className="py-2.5 px-3">
          <div className="flex items-center gap-1.5 mb-1">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: PRIORITY_DOT[request.priority] || PRIORITY_DOT.normal }}
              title={`Priorität: ${request.priority}`}
            />
            <Badge variant="outline" className="text-[9px] px-1 py-0" style={{ borderColor: request.type_color }}>
              {request.type_name}
            </Badge>
            {request.is_draft && <Badge variant="outline" className="text-[9px] px-1 py-0">Entwurf</Badge>}
          </div>
          <p className="text-sm font-medium line-clamp-2 mb-1">{title}</p>
          <div className="flex items-center justify-between text-[10px] text-gray-500">
            <div className="flex items-center gap-1 truncate">
              <UserIcon className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{request.creator_name}</span>
            </div>
            {request.created_at && (
              <div className="flex items-center gap-1 flex-shrink-0">
                <Clock className="w-3 h-3" />
                {format(new Date(request.created_at), 'dd.MM.', { locale: de })}
              </div>
            )}
          </div>
          {request.assignee_name && (
            <div className="text-[10px] text-gray-400 mt-1 truncate">→ {request.assignee_name}</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const KanbanColumn = ({ status, requests, onOpen }) => {
  const { setNodeRef, isOver } = useDroppable({ id: status.id });
  return (
    <div
      ref={setNodeRef}
      data-testid={`kanban-column-${status.id}`}
      className={`flex-shrink-0 w-72 rounded-lg p-2 transition-colors ${
        isOver ? 'bg-blue-50 dark:bg-blue-950/30 ring-2 ring-blue-300' : 'bg-gray-50 dark:bg-gray-900'
      }`}
    >
      <div className="flex items-center justify-between mb-3 px-1 sticky top-0">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: status.color }} />
          <span className="font-semibold text-sm">{status.name}</span>
          <span className="text-xs text-gray-400">{requests.length}</span>
        </div>
      </div>
      <div className="space-y-1 min-h-[60px]">
        {requests.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-4 italic">Leer</p>
        ) : (
          requests.map(r => <DraggableCard key={r.id} request={r} onOpen={onOpen} />)
        )}
      </div>
    </div>
  );
};

const AnfragenKanban = ({ requests, types, onOpen, onUpdated }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );
  const [activeRequest, setActiveRequest] = useState(null);

  // Choose statuses to display: union of all statuses from all type definitions
  // If only one type filter is applied, we could show that type's statuses.
  // Here we use a flat unified list — fall back to default statuses.
  const statuses = useMemo(() => {
    const map = new Map();
    types.forEach(t => {
      (t.statuses || []).forEach(s => {
        if (!map.has(s.id)) map.set(s.id, s);
      });
    });
    // Sort by sort_order if available
    return Array.from(map.values()).sort((a, b) => (a.sort_order ?? 99) - (b.sort_order ?? 99));
  }, [types]);

  const byStatus = useMemo(() => {
    const m = {};
    statuses.forEach(s => { m[s.id] = []; });
    m._unassigned = [];
    requests.forEach(r => {
      const sid = r.status_id;
      if (sid && m[sid]) m[sid].push(r);
      else m._unassigned.push(r);
    });
    return m;
  }, [statuses, requests]);

  const onDragStart = (e) => {
    setActiveRequest(e.active?.data?.current?.request || null);
  };

  const onDragEnd = async (e) => {
    setActiveRequest(null);
    const { active, over } = e;
    if (!over) return;
    const newStatusId = over.id;
    const req = active?.data?.current?.request;
    if (!req || req.status_id === newStatusId || newStatusId === '_unassigned') return;
    // Optimistic update via callback
    try {
      await axios.patch(`/anfragen/requests/${req.id}`, { status_id: newStatusId });
      const newStatus = statuses.find(s => s.id === newStatusId);
      toast.success(`Status: ${newStatus?.name || newStatusId}`);
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Status-Update fehlgeschlagen');
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <div className="overflow-x-auto pb-4" data-testid="kanban-board">
        <div className="flex gap-3 min-h-[400px]">
          {statuses.map(s => (
            <KanbanColumn key={s.id} status={s} requests={byStatus[s.id] || []} onOpen={onOpen} />
          ))}
          {(byStatus._unassigned?.length || 0) > 0 && (
            <KanbanColumn
              status={{ id: '_unassigned', name: 'Ohne Status', color: '#9ca3af' }}
              requests={byStatus._unassigned}
              onOpen={onOpen}
            />
          )}
        </div>
      </div>
      <DragOverlay>
        {activeRequest ? (
          <Card className="w-72 shadow-2xl ring-2 ring-blue-400">
            <CardContent className="py-2.5 px-3">
              <p className="text-sm font-medium line-clamp-2">
                {Object.values(activeRequest.form_data || {})[0] || 'Ohne Titel'}
              </p>
            </CardContent>
          </Card>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default AnfragenKanban;
